// Web Push helper. Client-side wrapper around Notification API + PushManager.
import api from "./api";

const OPTOUT_KEY = "ass_push_optout_v1";

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPhone|iPad|iPod/.test(ua) && !("MSStream" in window);
}

export function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari legacy flag
    !!window.navigator.standalone
  );
}

export function iosNeedsInstall() {
  // On iOS, Web Push only works from a home-screen-installed PWA (Safari 16.4+).
  return isIOS() && !isStandalone();
}

export function optedOut() {
  try {
    return localStorage.getItem(OPTOUT_KEY) === "1";
  } catch (_) {
    return false;
  }
}

export function setOptedOut(v) {
  try {
    if (v) localStorage.setItem(OPTOUT_KEY, "1");
    else localStorage.removeItem(OPTOUT_KEY);
  } catch (_) {}
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) output[i] = rawData.charCodeAt(i);
  return output;
}

async function getRegistration() {
  const reg =
    (await navigator.serviceWorker.getRegistration()) ||
    (await navigator.serviceWorker.ready);
  return reg;
}

export async function currentSubscription() {
  if (!isPushSupported()) return null;
  const reg = await getRegistration();
  return (await reg.pushManager.getSubscription()) || null;
}

export async function subscribePush() {
  if (!isPushSupported()) throw new Error("Push non supporté sur ce navigateur");
  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("Autorisation refusée");
  const { data } = await api.get("/push/public-key");
  const reg = await getRegistration();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.public_key),
    });
  }
  const raw = sub.toJSON();
  await api.post("/push/subscribe", {
    endpoint: raw.endpoint,
    keys: { p256dh: raw.keys.p256dh, auth: raw.keys.auth },
    user_agent: navigator.userAgent,
  });
  setOptedOut(false);
  return sub;
}

export async function unsubscribePush() {
  if (!isPushSupported()) return;
  const reg = await getRegistration();
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    try {
      await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
    } catch (_) {}
    try {
      await sub.unsubscribe();
    } catch (_) {}
  }
  setOptedOut(true);
}

export async function sendTestPush() {
  const { data } = await api.post("/push/test");
  return data;
}
